import { Box, ColorOptions, convertHexToRGBA, ModBox, RadioButton, Spacer, Text } from '@jfs/ui-new'
import { isEmpty } from '@jfs/utils'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Keyboard, TextInput as RNTextInput } from 'react-native'
import { CALENDER_ID, dateRangeData } from '../../data/static/data'
import { actionMenuStrings } from '../../data/static/strings'
import { Colors, getYesterday } from '../../util/util'
import { TableComponent } from '../TableComponent'
import { TextInputWithLabelOnBorder } from '../TextInputWithLabelOnBorder'
import {
  fromDateLessThanToDate,
  isLatestSixMonth,
  lessThanToday,
  oneMonthValidation,
  validateDateOnBlur,
} from './helper'

const convertDate = (date) => {
  const dateArr = date?.split('/') ?? []
  return new Date(+dateArr?.[2], Number(dateArr?.[1]) - 1, +dateArr?.[0])
}
interface DateRangeSelectorInterface {
  onDateRangeChange?: (value: any) => void
  selectedDateInfo?: any
  dateRange?: any
  type?: 'EXPORT' | 'FILTERS'
  selectedFilteredData?
}
const START_DATE = 'START_DATE'
const END_DATE = 'END_DATE'

const regex = /^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[012])[\/\-]\d{4}$/

const DateRangeSelector: FC<DateRangeSelectorInterface> = ({
  onDateRangeChange,
  selectedDateInfo,
  type = 'FILTERS',
  selectedFilteredData,
  dateRange = dateRangeData,
}: DateRangeSelectorInterface) => {
  const [customRangeInputsVisible, setCustomRangeInputsVisible] = useState(false)
  const updateDateRangeSelection = useCallback((value) => onDateRangeChange(value), [onDateRangeChange])
  const calender = selectedFilteredData?.selectedDateInfo ?? selectedDateInfo
  const [checkedDateRange, setCheckedDateRange] = useState(calender?.dateLabel)

  useEffect(() => {
    if (calender.dateLabel === 'Custom Range') {
      setCustomRangeInputsVisible(true)
    }
  }, [calender.dateLabel, selectedFilteredData])

  const startDateRef = useRef<RNTextInput>(null)
  const endDateRef = useRef<RNTextInput>(null)
  const errorRef = useRef<any>(null)

  const [dateError, setDateError] = useState('')
  const [customStartDate, setStartDate] = useState('')
  const [customEndDate, setEndDate] = useState('')

  const getTodayValue = (): number => {
    const today = new Date(Date.now())
    return new Date(today.setHours(0, 0, 0, 0))?.getTime()
  }

  const getDateValue = (value: number): number => {
    const today = new Date(Date.now())
    const ret = new Date(today.setDate(today.getDate() - value))?.getTime()
    return ret
  }

  const getYesterDayValue = () => {
    onDateRangeChange &&
      onDateRangeChange({
        fromDate: getYesterday('START'),
        toDate: getYesterday('END'),
        dateLabel: 'Yesterday',
      })
  }

  const updateRange = (value: string): void => {
    const getSelectedValue = {
      [actionMenuStrings.today]: getTodayValue(),
      [actionMenuStrings.lastSeven]: getDateValue(7),
      [actionMenuStrings.customDate]: getDateValue(1),
      [actionMenuStrings.lastFifteen]: getDateValue(15),
      [actionMenuStrings.lastThirty]: getDateValue(30),
      [actionMenuStrings.lastNinty]: getDateValue(90),
    }
    updateDateRangeSelection({
      fromDate: getSelectedValue[value],
      toDate: new Date(Date.now())?.getTime(),
      dateLabel: value,
    })
  }

  const resetCustomCalender = (message: any) => {
    onDateRangeChange({ fromDate: '', toDate: '', dateLabel: '' })
    setDateError(message)
  }

  const renderListItem = ({ item }: any) => {
    const handlePress = () => {
      Keyboard.dismiss()
      setCheckedDateRange(item?.value)
      if (item.value === actionMenuStrings.yesterday) {
        setStartDate('')
        setEndDate('')
        getYesterDayValue()
      } else if (item?.id !== CALENDER_ID) {
        setStartDate('')
        setEndDate('')
        updateRange(item?.value)
      } else {
        updateDateRangeSelection({ fromDate: null, toDate: null, dateLabel: item?.value })
      }
      setCustomRangeInputsVisible(item?.id === CALENDER_ID)
    }

    const validateText = (setTextFn, val, previousValue, DATE_TYPE) => {
      setDateError('')
      const numberWithoutChars = val.replace(/[^0-9]/g, '')
      const currentDate = new Date(Date.now())
      const currentYearThreeDigits = currentDate.getFullYear().toString().substring(0, 3)

      let formatedText = numberWithoutChars
      let dd = `${formatedText.substring(0, 2)}`
      let mm = `${formatedText.substring(2, 4)}`
      let yyyy = `${formatedText.substring(4, 8)}`
      //console.log('formatedText', formatedText, previousValue, val)
      // entering new text
      if (previousValue?.length < val?.length) {
        if (val?.length < 3) {
          if (val?.length === 1 && parseInt(dd, 10) > 3) {
            formatedText = `0${dd}/`
          } else if (val?.length === 2) {
            if (parseInt(val, 10) > 31) {
              formatedText = ''
              resetCustomCalender('Invalid date entered.')
            } else {
              formatedText = `${dd}/`
            }
          }
        }
        //mm
        if (val?.length >= 3 && val?.length < 6) {
          if (parseInt(mm, 10) > 12) {
            resetCustomCalender('Invalid month entered.')
            formatedText = `${dd}/`
          } else if (val?.length === 5) {
            formatedText = `${dd}/${mm}/${currentYearThreeDigits}`
          } else if ((val?.length === 3 || val?.length === 4) && parseInt(mm, 10) >= 2) {
            formatedText = `${dd}/0${formatedText.substring(2, 3)}/`
          } else {
            formatedText = `${dd}/${mm}`
          }
        }

        //yyyy
        if (val?.length >= 6) {
          if (val?.length === 10 && parseInt(yyyy, 10) <= currentDate?.getFullYear()) {
            formatedText = `${dd}/${mm}/${yyyy}`
            if (!isEmpty(formatedText) && !!regex?.test(formatedText) && isValidDate(formatedText)) {
              const date: any = convertDate(formatedText)
              if (!isLatestSixMonth(date)) {
                formatedText = ''
                resetCustomCalender('Date should not be older than 6 months.')
              } else if (!lessThanToday(date)) {
                formatedText = ''
                resetCustomCalender('Date should be less than today.')
              }
            } else {
              formatedText = ''
              resetCustomCalender('Invalid date entered.')
            }
          } else if (parseInt(yyyy, 10) > currentDate?.getFullYear()) {
            formatedText = `${dd}/${mm}/`
            resetCustomCalender('Invalid year entered.')
          } else {
            formatedText = `${dd}/${mm}/${yyyy}`
          }
        }
      } else {
        // on clear input values
        formatedText = previousValue.substring(0, previousValue?.length - 1)
      }

      // validate from and todate
      //setTextFn(formatedText)
      validateFromAndToDate(formatedText, DATE_TYPE, setTextFn)
    }

    const validateFromAndToDate = (formatedText: string, DATE_TYPE: string, setTextFn?: any) => {
      // validate this date with other specs
      const startDate = DATE_TYPE === START_DATE ? formatedText : customStartDate
      const endDate = DATE_TYPE === END_DATE ? formatedText : customEndDate

      //validate this with end date
      if (
        !isEmpty(startDate) &&
        !!regex?.test(startDate) &&
        !isEmpty(endDate) &&
        !!regex?.test(endDate) &&
        isValidDate(startDate) &&
        isValidDate(endDate)
      ) {
        const fromDate: any = convertDate(startDate)
        const toDate: any = convertDate(endDate)

        if (!fromDateLessThanToDate(fromDate, toDate)) {
          resetCustomCalender('Start date should be less than end date.')
          formatedText = ''
        } else if (!oneMonthValidation(fromDate, toDate)) {
          resetCustomCalender('Start and End date should not exceed more than 31 days.')
          formatedText = ''
        } else {
          onDateRangeChange({
            fromDate: new Date(fromDate)?.getTime(),
            toDate: new Date(toDate)?.getTime(),
            dateLabel: 'Custom Range',
          })
        }
      } else {
        onDateRangeChange({
          fromDate: '',
          toDate: '',
          dateLabel: '',
        })
      }
      setTextFn(formatedText)
    }

    //console.log('calendar', selectedFilteredData, customStartDate, customEndDate)
    return (
      <>
        <Box
          ph={12}
          onPress={handlePress}
          testID={'date-range-' + item?.value}
          underlayColor={'TRANSPARENT'}
          flexDirection={'row'}
          align={'center'}>
          <RadioButton checked={checkedDateRange === item.value} size={16} onChange={handlePress} />
          <Spacer direction="horizontal" size={8} />
          <Text numberOfLines={1} fontFamily={'JioType-Medium'} fontSize={14} lineHeight={20}>
            {item?.value ?? ''}
          </Text>
        </Box>
        {customRangeInputsVisible && item?.id === CALENDER_ID && checkedDateRange === 'Custom Range' && (
          <>
            <Spacer direction="vertical" size={20} />
            <Box ph={24} flexDirection={type === 'EXPORT' ? 'row' : 'column'}>
              <Box f={type === 'EXPORT' ? 1 : undefined} ml={type === 'EXPORT' ? 24 : 0}>
                <TextInputWithLabelOnBorder
                  fieldName={'startDate'}
                  setFieldValue={(_, val) => validateText(setStartDate, val, customStartDate, START_DATE)}
                  value={customStartDate}
                  label={'Start Date'}
                  height={52}
                  labelFontSize={14}
                  fontSize={14}
                  placeholderTextColor={convertHexToRGBA(Colors.BLACK_14, 50) as ColorOptions}
                  placeholder={'DD/MM/YYYY'}
                  fontFamily={'JioType-Medium'}
                  maxLength={10}
                  onBlur={() =>
                    validateDateOnBlur(setStartDate, 'start date', customStartDate, startDateRef, resetCustomCalender)
                  }
                  autoFocus={customStartDate ? false : true}
                  letterSpacing={-0.08}
                  lineHeight={20}
                  innerRef={startDateRef}
                  selectionColor={Colors.PRIMARY}
                  keyboardType="number-pad"
                />
              </Box>
              <Spacer direction={type === 'EXPORT' ? 'horizontal' : 'vertical'} size={20} />
              <Box f={type === 'EXPORT' ? 1 : undefined}>
                <TextInputWithLabelOnBorder
                  fieldName={'endDate'}
                  value={customEndDate}
                  setFieldValue={(_, val) => validateText(setEndDate, val, customEndDate, END_DATE)}
                  label={'End Date'}
                  height={52}
                  labelFontSize={14}
                  fontSize={14}
                  placeholder={'DD/MM/YYYY'}
                  placeholderTextColor={convertHexToRGBA(Colors.BLACK_14, 50) as ColorOptions}
                  fontFamily={'JioType-Medium'}
                  maxLength={10}
                  letterSpacing={-0.08}
                  lineHeight={20}
                  onBlur={() =>
                    validateDateOnBlur(setEndDate, 'end date', customEndDate, endDateRef, resetCustomCalender)
                  }
                  innerRef={endDateRef}
                  selectionColor={Colors.PRIMARY}
                  keyboardType="number-pad"
                />
              </Box>
              {/* <Spacer direction="vertical" size={20} /> */}
            </Box>
            <ModBox pl={type === 'EXPORT' ? 54 : 25} pt={8} minHeight={35}>
              <Text
                color={'#E30513' as ColorOptions}
                fontFamily={'JioType-Medium'}
                fontSize={12}
                lineHeight={16}
                hide={isEmpty(dateError)}
                letterSpacing={-0.06}>
                {dateError}
              </Text>
              <Spacer direction="vertical" size={20} />
            </ModBox>
          </>
        )}
      </>
    )
  }
  const keyExtractor = (item: any): string => {
    return item?.id
  }

  return (
    <TableComponent
      value={''} //Routes value
      canFetchMore={false}
      ListContent={dateRange}
      hide={false}
      isCustom={true}
      RenderCustomElement={renderListItem}
      keyExtractor={keyExtractor}
    />
  )
}

// validate entered date is valid and leaf year validation passed
const isValidDate = (dateString: any) => {
  //dd/mm/yyyy
  // Parse the date parts to integers
  var parts = dateString?.split('/')
  var day = parseInt(parts[0], 10)
  var month = parseInt(parts[1], 10)
  var year = parseInt(parts[2], 10)

  // Check the ranges of month and year
  if (year < 1000 || year > 3000 || month === 0 || month > 12) {
    return false
  }

  var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  // Adjust for leap years
  if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
    monthLength[1] = 29
  }

  // Check the range of the day
  return day > 0 && day <= monthLength[month - 1]
}

export { DateRangeSelector }
